import React, { useRef, useState } from 'react'
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createAdmin, editAdmin } from '../../../api/AdminRequest';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { CloseOutlined, MinusCircleOutlined, PlusCircleOutlined, PlusOutlined } from '@ant-design/icons';

function CreateAdmin({ edit }) {

  const navigate = useNavigate();
  const { state } = useLocation();

  if (state?.drawerData?.createdDate && state?.drawerData?.expiryDate) {
    const createdDate = dayjs(state.drawerData.createdDate);
    const expiryDays = state.drawerData.expiryDate;
    const calculatedExpiryDate = createdDate.add(expiryDays, 'days');
    console.log('Calculated Expiry Date:', calculatedExpiryDate.format('MMMM DD, YYYY'));
    console.log('Calculated Expiry Date (Full):', calculatedExpiryDate.format('LLLL'));
  }

  const [adminData] = useState(edit ? state.drawerData : '');
  const initial = [{ type: null, limit: "" }];

  const [featuresList, setFeaturesList] = useState(edit ? state?.drawerData?.features : initial);
  const [showDomainInput, setShowDomainInput] = useState(edit ? !!state?.drawerData?.domain : false);

  const [name, setName] = useState('');
  const defaultItems = [
    { name: "Business Card", value: "businessCard" },
    { name: "Review Card", value: "reviewCard" },
    { name: "Feedback Card", value: "feedbackCard" },
    { name: "Contact Card", value: "contactCard" },
  ];
  const [items, setItems] = useState(defaultItems);

  const [phone, setPhone] = useState(edit ? String(state?.drawerData?.phone || '') : '971');
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  const onFinish = async (values) => {
    values.phone = phone;
    values.features = featuresList;

    try {
      const { data } = edit
        ? await editAdmin(adminData._id, values)
        : await createAdmin(values);

      if (data.success) {
        toast.success('New Admin Created', {
          position: "top-right",
          autoClose: 3000,
          theme: "dark",
        });
      } else if (data.update) {
        toast.success('Updated Successfully', {
          position: "top-right",
          autoClose: 3000,
          theme: "dark",
        });
      }
      setTimeout(() => navigate('/view-admins'), 3000);
    } catch (error) {
      toast(error.response.data.message);
    }
  };

  const passRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const addFormFields = () => {
    setFeaturesList([...featuresList, { type: null, limit: "" }]);
  };

  const removeFormFields = (i) => {
    let newFormValues = [...featuresList];
    newFormValues.splice(i, 1);
    setFeaturesList(newFormValues);
  };

  const onNameChange = (event) => {
    setName(event.target.value);
  };

  const addItem = (e) => {
    e.preventDefault();
    if (!name || items.some(item => item?.value === name)) return;
    setItems([...items, { name: name, value: name }]);
    setName('');
    setTimeout(() => { inputRef.current?.focus(); }, 0);
  };

  const removeItem = (removedItem) => {
    const updatedItems = items.filter((item) => item.value !== removedItem.value);
    setItems(updatedItems);
  };

  const getOptionLabel = (selectedItem) => {
    if (defaultItems.some((item) => item.value === selectedItem.value)) {
      return <span>{selectedItem.name}</span>;
    }
    return (
      <div className='flex justify-between items-center'>
        <p>{selectedItem.name}</p>
        <CloseOutlined
          onClick={() => removeItem(selectedItem)}
          className="cursor-pointer ml-2 text-slate-400 hover:text-red-500"
        />
      </div>
    );
  };

  const handleChangeCardType = (field, index, value) => {
    const updatedFeaturesList = [...featuresList];
    updatedFeaturesList[index][field] = value;
    setFeaturesList(updatedFeaturesList);
  };

  const onDomainChange = (e) => {
    setShowDomainInput(e.target.checked);
    if (!e.target.checked) {
      form.setFieldValue('domain', '');
    }
  };

  const AdminIcon = () => (
    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      {edit ? (
        <>
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </>
      ) : (
        <>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M9 12l2 2 4-4" />
        </>
      )}
    </svg>
  );

  const FieldLabel = ({ children }) => (
    <span className="block text-sm font-semibold text-slate-600 mb-1.5">
      {children}
    </span>
  );

  const SectionLabel = ({ children }) => (
    <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-slate-400 mb-4 pb-3 border-b border-slate-100">
      <span className="w-1.5 h-1.5 rounded-full bg-blue-800 flex-shrink-0" />
      {children}
    </div>
  );

  return (
    <>
      {/* Minimal overrides only for Ant Design internals that Tailwind can't reach */}
      <style>{`
        .cu-root { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        .cu-root .ant-input,
        .cu-root .ant-input-affix-wrapper {
          border-radius: 10px !important; border: 1.5px solid #e2e8f0 !important;
          background: #f8fafc !important; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
          font-size: 14px !important; padding: 10px 14px !important;
        }
        .cu-root .ant-input-affix-wrapper { padding: 0 14px !important; }
        .cu-root .ant-input-affix-wrapper .ant-input {
          border: none !important; box-shadow: none !important;
          background: transparent !important; padding: 10px 0 !important;
        }
        .cu-root .ant-input:focus,
        .cu-root .ant-input-affix-wrapper-focused {
          border-color: #1e3a8a !important;
          box-shadow: 0 0 0 3px rgba(30,58,138,0.1) !important;
          background: #fff !important;
        }
        .cu-root .ant-select-selector {
          border-radius: 10px !important; border: 1.5px solid #e2e8f0 !important;
          background: #f8fafc !important; min-height: 42px !important;
          display: flex !important; align-items: center !important;
          padding: 0 14px !important; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
          font-size: 14px !important;
        }
        .cu-root .ant-select-focused .ant-select-selector {
          border-color: #1e3a8a !important;
          box-shadow: 0 0 0 3px rgba(30,58,138,0.1) !important;
        }
        .cu-root .ant-form-item { margin-bottom: 0 !important; }
        .cu-root .ant-picker {
          border-radius: 10px !important; border: 1.5px solid #e2e8f0 !important;
          background: #f8fafc !important; padding: 10px 14px !important;
        }
        .cu-root .ant-picker-focused {
          border-color: #1e3a8a !important;
          box-shadow: 0 0 0 3px rgba(30,58,138,0.1) !important;
          background: #fff !important;
        }
        .cu-root .react-tel-input .form-control {
          border-radius: 10px !important; border: 1.5px solid #e2e8f0 !important;
          height: 42px !important; width: 100% !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important; font-size: 14px !important;
          background: #f8fafc !important; padding-left: 52px !important; color: #0f172a !important;
        }
        .cu-root .react-tel-input .form-control:focus {
          border-color: #1e3a8a !important;
          box-shadow: 0 0 0 3px rgba(30,58,138,0.1) !important; background: #fff !important;
        }
        .cu-root .react-tel-input .flag-dropdown {
          border-radius: 10px 0 0 10px !important; border: 1.5px solid #e2e8f0 !important;
          border-right: none !important; background: #f1f5f9 !important;
        }
      `}</style>

      <div className="cu-root min-h-screen flex flex-col justify-center items-center px-8 py-10">

        {/* ── Page Header ── */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-blue-800 flex items-center justify-center shadow-lg shadow-blue-900/25 flex-shrink-0">
            <AdminIcon />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 leading-none mb-1">
              {edit ? 'Edit Admin' : 'Create Admin'}
            </h1>
            <p className="text-sm text-slate-400 font-normal m-0">
              {edit
                ? 'Update the details for this admin account.'
                : 'Configure a new admin with custom features and permissions.'}
            </p>
          </div>
        </div>

        {/* ── Card ── */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 max-w-7xl w-full">
          <Form
            form={form}
            name="register"
            onFinish={onFinish}
            initialValues={{
              name: adminData.name,
              email: adminData.email,
              phone: edit ? String(adminData.phone || '') : '971',
              expiryDays: adminData.expiryDate,
              domain: adminData.domain,
            }}
            scrollToFirstError
            className='w-full'
          >

            {/* Section: Account Info */}
            <SectionLabel>Account Information</SectionLabel>

            <div className="grid grid-cols-2 w-full gap-5 mb-5">

              <div className="flex flex-col">
                <FieldLabel>Full Name</FieldLabel>
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: 'Please input your name!', whitespace: true }]}
                >
                  <Input placeholder="e.g. John Admin" />
                </Form.Item>
              </div>

              <div className="flex flex-col">
                <FieldLabel>Email Address</FieldLabel>
                <Form.Item
                  name="email"
                  rules={[
                    { type: 'email', message: 'The input is not valid E-mail!' },
                    { required: true, message: 'Please input your E-mail!' },
                  ]}
                >
                  <Input placeholder="e.g. admin@example.com" />
                </Form.Item>
              </div>

              <div className="flex flex-col">
                <FieldLabel>Phone Number</FieldLabel>
                <Form.Item
                  name="phone"
                  rules={[
                    { required: true, message: 'Please input your phone number!' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        // Ensure value is a string before processing
                        const phoneStr = typeof value === 'string' ? value : String(value || '');
                        if (!phoneStr) return Promise.resolve();
                        
                        // Extract only digits from the phone value
                        const digitsOnly = phoneStr.replace(/\D/g, '');
                        // Valid phone numbers should have at least 8 digits
                        if (digitsOnly.length < 8) {
                          return Promise.reject(
                            new Error('Phone number is invalid!')
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  hasFeedback
                >
                  <PhoneInput
                    country={"eg"}
                    enableSearch={true}
                    value={phone}
                    onChange={(value) => {
                      const phoneStr = String(value || '');
                      setPhone(phoneStr);
                      form.setFieldValue('phone', phoneStr);
                    }}
                  />
                </Form.Item>
              </div>

              <div className="flex flex-col">
                <FieldLabel>Expiry Date</FieldLabel>
                <Form.Item
                  name="expiryDays"
                  rules={[{ required: true, message: 'Please select Expiry Date!' }]}
                  getValueFromEvent={(date) => {
  if (!date) return null;
  const base = edit
    ? dayjs(state?.drawerData?.createdDate)  // ← diff from original createdDate
    : dayjs();
  return Math.ceil(date.diff(base, 'days', true));
}}
                  getValueProps={(value) => ({
                    value: typeof value === 'number'
                      ? dayjs(state?.drawerData?.createdDate).add(value, 'days')  // ← use createdDate, not dayjs()
                      : value,
                  })}
                >
                  <DatePicker
                    placeholder="Select Expiry Date"
                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                    className="w-full"
                  />
                </Form.Item>
              </div>

            </div>

            {/* Section: Features */}
            <div className="border-t border-slate-100 my-6" />
            <SectionLabel>Features & Permissions</SectionLabel>

            <div className="mb-5">
              <Checkbox onChange={onDomainChange} checked={showDomainInput} className='mb-3'>
                <span className="text-sm font-medium text-slate-600">White Label (Custom Domain)</span>
              </Checkbox>

              {showDomainInput && (
                <div className="flex flex-col mt-3">
                  <FieldLabel>Domain Name</FieldLabel>
                  <Form.Item
                    name="domain"
                    rules={[{ required: true, message: 'Please input your domain name!' }]}
                  >
                    <Input placeholder="e.g. https://yourdomain.com" />
                  </Form.Item>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {featuresList?.map((element, index) => (
                <div className="grid gap-5 md:grid-cols-2 p-4 bg-slate-50 rounded-xl border border-slate-100" key={index}>

                  <div className="flex flex-col">
                    <FieldLabel>Card Type</FieldLabel>
                    <Select
                      value={element?.type}
                      size='large'
                      placeholder="Select Card Type"
                      onChange={(value) => handleChangeCardType('type', index, value)}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <Divider className="my-2" />
                          <Space className="px-2 pb-1">
                            <Input
                              placeholder="Enter custom card type"
                              ref={inputRef}
                              value={name}
                              onChange={onNameChange}
                              onKeyDown={(e) => e.stopPropagation()}
                              className="text-sm"
                            />
                            {(!name || items.some(item => item.value === name))
                              ? <Button type="text" disabled icon={<PlusOutlined />}>Add</Button>
                              : <Button type="text" icon={<PlusOutlined />} onClick={addItem}>Add</Button>
                            }
                          </Space>
                        </>
                      )}
                    >
                      {items?.map((item) => (
                        <Select.Option
                          key={item.value}
                          value={item.value}
                          disabled={featuresList.some((feature) => feature.type === item.value)}
                        >
                          {getOptionLabel(item)}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>

                  <div className="flex gap-3 w-full items-end">
                    <div className="flex-1">
                      {element?.type === 'feedbackCard' ? (
                        <div className='grid grid-cols-2 gap-3'>
                          <div className="flex flex-col">
                            <FieldLabel>Manager Limit</FieldLabel>
                            <Input
                              type="text"
                              placeholder="e.g. 10"
                              value={element?.serviceMangerLimit || ""}
                              onChange={(e) => handleChangeCardType('serviceMangerLimit', index, e.target.value)}
                            />
                          </div>
                          <div className="flex flex-col">
                            <FieldLabel>Employee Limit</FieldLabel>
                            <Input
                              type="text"
                              placeholder="e.g. 50"
                              value={element?.employeeLimit || ""}
                              onChange={(e) => handleChangeCardType('employeeLimit', index, e.target.value)}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          <FieldLabel>Card Limit</FieldLabel>
                          <Input
                            type="text"
                            placeholder="e.g. 100"
                            value={element?.limit || ""}
                            onChange={(e) => handleChangeCardType('limit', index, e.target.value)}
                          />
                        </div>
                      )}
                    </div>

                    {index ? (
                      <button type="button" className="h-10 px-3 rounded-lg hover:bg-red-50 transition-colors" onClick={() => removeFormFields(index)}>
                        <Tooltip placement="top" title="Remove">
                          <MinusCircleOutlined className="text-xl text-red-500" />
                        </Tooltip>
                      </button>
                    ) : (
                      <button type="button" className="h-10 px-3 rounded-lg hover:bg-green-50 transition-colors" onClick={() => addFormFields()}>
                        <Tooltip placement="top" title="Add More">
                          <PlusCircleOutlined className="text-xl text-green-500" />
                        </Tooltip>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Section: Security — create mode only */}
            {!edit && (
              <>
                <div className="border-t border-slate-100 my-6" />
                <SectionLabel>Security</SectionLabel>

                <div className="grid grid-cols-2 gap-5 mb-5">

                  <div className="flex flex-col">
                    <FieldLabel>Password</FieldLabel>
                    <Form.Item
                      name="password"
                      rules={[
                        { required: true, message: 'Please input your password!' },
                        {
                          pattern: passRegex,
                          message: 'Min 8 chars, one uppercase, one number & one special character.',
                        },
                      ]}
                      hasFeedback
                    >
                      <Input.Password placeholder="Create a strong password" />
                    </Form.Item>
                  </div>

                  <div className="flex flex-col">
                    <FieldLabel>Confirm Password</FieldLabel>
                    <Form.Item
                      name="confirm"
                      dependencies={['password']}
                      hasFeedback
                      rules={[
                        { required: true, message: 'Please confirm your password!' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match!'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password placeholder="Repeat your password" />
                    </Form.Item>
                  </div>

                </div>

            
              </>
            )}

            {/* ── Actions ── */}
            <div className="border-t border-slate-100 mt-6 pt-6 flex items-center gap-3">
              <Button
                type="primary"
                htmlType="submit"
                className="!bg-blue-800 !border-none !rounded-xl !h-10 !px-7 !text-sm !font-semibold !tracking-wide !shadow-md hover:!bg-blue-900 hover:!-translate-y-px !transition-all !duration-150"
              >
                {edit ? 'Save Changes' : 'Register Admin'}
              </Button>
              <Button
                className="!rounded-xl !h-10 !px-7 !text-sm !font-medium !text-slate-500 !border !border-slate-200 !bg-white hover:!border-slate-300 hover:!text-slate-800 !transition-all !duration-150"
                onClick={() => navigate('/view-admins')}
              >
                {edit ? 'Cancel' : 'Go Back'}
              </Button>
            </div>

          </Form>
        </div>
      </div>

      <ToastContainer />
    </>
  );
}

export default CreateAdmin;